<template>
  <div class="storage-packs-tab">
    <b-card no-body>
      <b-table
        :items="rows"
        :fields="tableColumns"
        primary-key="id"
        responsive
        show-empty
        empty-text="Пакетов нет"
        class="position-relative mb-0"
        :busy="loading"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-50" />
            <strong>Загрузка...</strong>
          </div>
        </template>

        <template #cell(name)="data">
          <b-link :to="{ name: 'question-storage-pack', params: { id: data.item.id } }" class="font-weight-bold">
            {{ data.item.name || `Пакет #${data.item.id}` }}
          </b-link>
        </template>

        <template #cell(type)="data">
          <b-badge :variant="data.item.type === 'imported' ? 'light-primary' : 'light-secondary'">
            {{ typeLabels[data.item.type] || data.item.type }}
          </b-badge>
        </template>

        <template #cell(planfix_pack)="data">
          <v-select
            v-if="canManage && canViewGames"
            :value="planfixValue(data.item)"
            :options="planfixPacks"
            :get-option-label="planfixOptionLabel"
            :selectable="pack => pack.id !== null"
            :loading="planfixPacksLoading || savingPackId === data.item.id"
            :disabled="savingPackId === data.item.id"
            placeholder="Не привязан"
            class="planfix-select"
            @input="option => attachPlanfixPack(data.item, option)"
          >
            <template #option="pack">
              {{ planfixOptionLabel(pack) }}
              <b-badge v-if="pack.id === null" variant="light-secondary" class="ml-50">нет в БД</b-badge>
            </template>
            <template #no-options>Ничего не найдено</template>
          </v-select>
          <span v-else-if="data.item.planfix_pack">{{ planfixOptionLabel(packOption(data.item.planfix_pack)) }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(premiere_at)="data">
          {{ formatDate(data.item.premiere_at) }}
        </template>

        <template #cell(last_played_at)="data">
          {{ formatDate(data.item.last_played_at) }}
        </template>

        <template #cell(tagged_percent)="data">
          <span :class="data.item.tagged_percent >= 100 ? 'text-success' : ''">{{ data.item.tagged_percent }}%</span>
        </template>

        <template #cell(actions)="data">
          <b-button
            v-if="canManage && data.item.type === 'imported'"
            variant="flat-danger"
            size="sm"
            class="btn-icon"
            title="Удалить пакет из хранилища"
            :disabled="deletingPackId === data.item.id"
            @click="confirmDelete(data.item)"
          >
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <label class="text-nowrap mb-0 mr-50 font-small-3">На странице:</label>
          <v-select
            :value="perPage"
            :options="perPageOptions"
            :clearable="false"
            style="min-width: 80px"
            @input="onPerPageInput"
          />
        </div>
        <span class="text-muted small">Всего: {{ total }}</span>
        <b-pagination
          v-if="total > 0"
          :value="page"
          :total-rows="total"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0"
          prev-class="prev-item"
          next-class="next-item"
          @input="onPageInput"
        />
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BCardBody, BLink, BPagination, BSpinner, BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { computed, getCurrentInstance, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

const MODULE = 'question-storage'
const DEFAULT_PER_PAGE = 30

const formatDate = value => {
  if (!value) return '—'
  const [year, month, day] = String(value).slice(0, 10).split('-')

  return `${day}.${month}.${year}`
}

// 422 бэкенда: первое сообщение из errors, иначе message
const errorMessage = (error, fallback) => {
  const data = error && error.response && error.response.data
  if (data && data.errors) {
    const first = Object.values(data.errors)[0]
    if (Array.isArray(first) && first.length) return first[0]
  }

  return (data && data.message) || fallback
}

export default {
  components: {
    BBadge, BButton, BCard, BCardBody, BLink, BPagination, BSpinner, BTable, vSelect,
  },
  setup(props, { root }) {
    const toast = useToast()
    const instance = getCurrentInstance().proxy
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })
    const showSuccess = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'CheckIcon', variant: 'success' },
    })

    const canManage = computed(() => instance.$can('manage', 'questions'))
    // GET /planfix-packs закрыт правом на раздел «Игры» — без него не запрашиваем, ПФ-пакет показываем текстом
    const canViewGames = instance.$can('view', 'games')

    const typeLabels = {
      imported: 'Импорт',
      'builder-music': 'Построитель: музыка',
      'builder-movies': 'Построитель: кино',
      'builder-series': 'Построитель: сериалы',
    }

    const tableColumns = [
      { key: 'name', label: 'Название' },
      { key: 'type', label: 'Тип' },
      { key: 'planfix_pack', label: 'ПФ-пакет' },
      { key: 'premiere_at', label: 'Премьера' },
      { key: 'last_played_at', label: 'Посл. отыгрыш' },
      { key: 'plays_count', label: 'Игр' },
      { key: 'questions_count', label: 'Вопросов' },
      { key: 'tagged_percent', label: 'Размечено' },
      { key: 'actions', label: '' },
    ]

    const rows = ref([])
    const total = ref(0)
    const page = ref(1)
    const perPage = ref(DEFAULT_PER_PAGE)
    const perPageOptions = [30, 50, 100]
    const loading = ref(false)
    const planfixPacks = ref([])
    const planfixPacksLoading = ref(false)
    const savingPackId = ref(null)
    const deletingPackId = ref(null)
    let fetchNumber = 0

    const fetchList = () => {
      fetchNumber += 1
      const requestNumber = fetchNumber
      loading.value = true
      store.dispatch(`${MODULE}/fetchPacks`, { page: page.value, per_page: perPage.value })
        .then(response => {
          if (requestNumber !== fetchNumber) return
          rows.value = response.data.data || []
          total.value = response.data.total || 0
        })
        .catch(() => {
          if (requestNumber === fetchNumber) showError('Не удалось загрузить пакеты')
        })
        .finally(() => {
          if (requestNumber === fetchNumber) loading.value = false
        })
    }
    fetchList()

    if (canManage.value && canViewGames) {
      planfixPacksLoading.value = true
      store.dispatch(`${MODULE}/fetchPlanfixPacks`)
        .then(response => { planfixPacks.value = response.data.packs || [] })
        .catch(() => showError('Не удалось загрузить ПФ-пакеты'))
        .finally(() => { planfixPacksLoading.value = false })
    }

    const onPageInput = value => {
      page.value = value
      fetchList()
    }

    const onPerPageInput = value => {
      perPage.value = value
      page.value = 1
      fetchList()
    }

    // Справочник ПФ отдаёт {id, key, name}, строка пакета — {id, planfix_key, name}
    const packOption = planfixPack => ({ id: planfixPack.id, key: planfixPack.planfix_key, name: planfixPack.name })
    const planfixOptionLabel = pack => (pack.key ? `${pack.key} · ${pack.name || 'Без названия'}` : (pack.name || `ПФ-пакет #${pack.id}`))
    const planfixValue = item => {
      if (!item.planfix_pack) return null

      return planfixPacks.value.find(pack => pack.id === item.planfix_pack.id) || packOption(item.planfix_pack)
    }

    const attachPlanfixPack = (item, option) => {
      const planfixPackId = option ? option.id : null
      if ((item.planfix_pack ? item.planfix_pack.id : null) === planfixPackId) return
      savingPackId.value = item.id
      store.dispatch(`${MODULE}/attachPlanfixPack`, { gameId: item.id, planfixPackId })
        .then(response => {
          const result = response.data.data
          Object.assign(item, {
            planfix_pack: option ? { id: option.id, planfix_key: option.key, name: option.name } : null,
            premiere_at: result.premiere_at,
            last_played_at: result.last_played_at,
            plays_count: result.plays_count,
          })
          showSuccess(option ? 'ПФ-пакет привязан, даты пересчитаны' : 'ПФ-пакет отвязан')
        })
        .catch(error => showError(errorMessage(error, 'Не удалось привязать ПФ-пакет')))
        .finally(() => { savingPackId.value = null })
    }

    const confirmDelete = async item => {
      const confirmed = await root.$bvModal.msgBoxConfirm(
        `Пакет «${item.name || `#${item.id}`}» будет удалён из хранилища вместе с ${item.questions_count} вопросами и их медиа. Отменить нельзя.`,
        {
          title: 'Удаление пакета',
          okTitle: 'Удалить',
          cancelTitle: 'Отмена',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return
      deletingPackId.value = item.id
      store.dispatch(`${MODULE}/deletePack`, item.id)
        .then(() => {
          showSuccess('Пакет удалён')
          if (rows.value.length === 1 && page.value > 1) page.value -= 1
          fetchList()
        })
        .catch(error => showError(errorMessage(error, 'Не удалось удалить пакет')))
        .finally(() => { deletingPackId.value = null })
    }

    return {
      canManage,
      canViewGames,
      typeLabels,
      tableColumns,
      rows,
      total,
      page,
      perPage,
      perPageOptions,
      loading,
      planfixPacks,
      planfixPacksLoading,
      savingPackId,
      deletingPackId,
      onPageInput,
      onPerPageInput,
      packOption,
      planfixOptionLabel,
      planfixValue,
      attachPlanfixPack,
      confirmDelete,
      formatDate,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.planfix-select {
  min-width: 260px;
}
</style>
